import { isAndroid13OrAbove } from '@bucketplace/common';
import { useSnackbar } from '@bucketplace/design-system';
import { copyText as copyTextLib } from '@bucketplace/lib';
import { usePlatformInfo } from '@/promotions/hooks/usePlatformInfo';

const shouldHideAndroidToast = async (cookie: string): Promise<boolean> => {
  // User-Agent Client Hints API로 실제 Android 버전 확인
  if ('userAgentData' in navigator) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const userAgentData = (await (
        navigator as any
      ).userAgentData.getHighEntropyValues(['platformVersion'])) as {
        platformVersion?: string;
      };
      if (userAgentData.platformVersion) {
        const version = parseInt(userAgentData.platformVersion, 10);
        return version >= 12; // Android 12부터 클립보드 접근 시 시스템 토스트
      }
    } catch {
      // 권한 거부 또는 미지원 시 fallback
      return isAndroid13OrAbove(cookie);
    }
  }

  // fallback: 기존 함수 결과 사용
  return isAndroid13OrAbove(cookie);
};

export const useCopyClipboard = (): {
  onCopy: (mutateAsync: () => Promise<string>) => Promise<void>;
} => {
  const { isMobile } = usePlatformInfo();
  const { open } = useSnackbar();

  let urlToCopy = '';

  const handleCopy = async (
    mutateAsync: () => Promise<string>,
  ): Promise<void> => {
    const isIos =
      navigator.userAgent.match(/ipad|ipod|iphone|safari/i) &&
      !navigator.userAgent.match(/android/i);

    /** @ https://wolfgangrittner.dev/how-to-use-clipboard-api-in-firefox/ */
    if (isIos && typeof ClipboardItem && navigator.clipboard.write) {
      // NOTE: Safari locks down the clipboard API to only work when triggered
      //   by a direct user interaction. You can't use it async in a promise.
      //   But! You can wrap the promise in a ClipboardItem, and give that to
      //   the clipboard API.
      //   Found this on https://developer.apple.com/forums/thread/691873
      const clipboardItem = new ClipboardItem({
        'text/plain': mutateAsync()
          .then((response) => response ?? window.location.href)
          .then((text) => {
            urlToCopy = text;
            return new Blob([text], { type: 'text/plain' });
          }),
      });
      navigator.clipboard
        .write([clipboardItem])
        .then(() => {
          return open({ content: '클립보드에 복사했습니다.' });
        })
        .catch(() => {
          try {
            copyTextLib(urlToCopy);
          } catch (err) {
            console.error('Could not copy text: ', err);
            return open({ content: '클립보드 복사에 실패했습니다.' });
          }
        });
    } else {
      // NOTE: Firefox has support for ClipboardItem and navigator.clipboard.write,
      //   but those are behind `dom.events.asyncClipboard.clipboardItem` preference.
      //   Good news is that other than Safari, Firefox does not care about
      //   Clipboard API being used async in a Promise.
      const response = await mutateAsync();
      urlToCopy = response ?? window.location.href;
      navigator.clipboard
        .writeText(urlToCopy)
        .then(async () => {
          const hideToast = await shouldHideAndroidToast(document.cookie);
          if (isMobile && hideToast) {
            return;
          }
          return open({ content: '클립보드에 복사했습니다.' });
        })
        .catch(() => {
          try {
            copyTextLib(urlToCopy);
          } catch (err) {
            console.error('Could not copy text: ', err);
            return open({ content: '클립보드 복사에 실패했습니다.' });
          }
        });
    }
  };

  return {
    onCopy: handleCopy,
  };
};
